import { Autocomplete, Box, TextField, Typography } from "@mui/material";
import { ModalControls, ModalType, showModal } from "components/Modal";
import TabbedButton from "components/TabbedButton";
import logger, { TYPE } from "logger";
import { API, get, post } from "modules/api";
import QuestionnaireForm from "modules/workflow/components/QuestionnaireForm";
import {
  QuestionType,
  TextFieldType,
} from "modules/workflow/types/Questionnaire";
import React from "react";
import { useDispatch } from "react-redux";
import {
  AnswersByQuestionnaireType,
  AnswersMap,
} from "../../../../workflow/types/Answer";
import ApiError from "../../../../api/types/ApiError";
import axios from "axios";
import {
  medicaidDraftingAnswersChanged,
  resetMedicaidDraftingForm,
} from "modules/partner/actions";
import { useSelector } from "redux/reducer";
import { clientFields } from "modules/parties/helpers/getClientQuestionnaire/constants";

import styles from "./styles.module.scss";
import classNames from "classnames/bind";

const cx = classNames.bind(styles);

type PartnerUserEmailSuggestion = {
  id: string;
  email: string;
  firstName?: string;
  middleName?: string;
  lastName?: string;
  phoneNumber: string;
  state?: string;
};

const populateMedicaidAnswers = (
  answers: AnswersMap,
  clientUserId: string,
) => ({
  jurisdiction: answers.state || "",
  clientUserId,
});

const MedicaidDraftAdd: React.FC = () => {
  const dispatch = useDispatch();
  const handleModalOpen = () => {
    dispatch(resetMedicaidDraftingForm());
    showMedicaidDraft();
  };

  return (
    <Box mb={2}>
      <TabbedButton onClick={handleModalOpen}>Add Medicaid Draft</TabbedButton>
    </Box>
  );
};

const showMedicaidDraft = () => {
  showModal({
    title: "Add Medicaid Draft",
    type: ModalType.CONFIRM,
    renderContent: (controls: ModalControls) => (
      <MedicaidDraftModalContent controls={controls} />
    ),
  });
};

type ModalProps = {
  controls: ModalControls;
};

const QUESTIONNAIRE_ID = "addMedicaidDraftModal";

const MedicaidDraftModalContent: React.FC<ModalProps> = ({ controls }) => {
  const dispatch = useDispatch();
  const [emailOptions, setEmailOptions] = React.useState<
    PartnerUserEmailSuggestion[]
  >([]);
  const [emailInputValue, setEmailInputValue] = React.useState("");
  const [selectedUser, setSelectedUser] =
    React.useState<PartnerUserEmailSuggestion | null>(null);
  const [isSearchingUsers, setIsSearchingUsers] = React.useState(false);

  const medicaidFormAnswers = useSelector(
    (state) => state.medicaidDraftingFormAnswers,
  );

  React.useEffect(() => {
    const query = emailInputValue.trim();
    if (query.length < 2) {
      setEmailOptions([]);
      setIsSearchingUsers(false);
      return;
    }

    const timeoutId = setTimeout(async () => {
      setIsSearchingUsers(true);
      try {
        const response = await get<PartnerUserEmailSuggestion[]>(
          API.PARTNER_USERS_SEARCH({ email: query }),
        );
        setEmailOptions(response.data || []);
      } catch {
        setEmailOptions([]);
      } finally {
        setIsSearchingUsers(false);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [emailInputValue]);

  const handleChange = (answersByQuestionnaire: AnswersByQuestionnaireType) =>
    answersByQuestionnaire[QUESTIONNAIRE_ID] !== undefined &&
    dispatch(
      medicaidDraftingAnswersChanged(answersByQuestionnaire[QUESTIONNAIRE_ID]),
    );

  const selectedUserPrefillAnswers: AnswersMap = selectedUser
    ? {
        [clientFields.firstName]: selectedUser.firstName || "",
        [clientFields.middleName]: selectedUser.middleName || "",
        [clientFields.lastName]: selectedUser.lastName || "",
        [clientFields.contact.cellPhone]: selectedUser.phoneNumber || "",
        state: selectedUser.state || "",
      }
    : {};

  const effectiveMedicaidFormAnswers: AnswersMap = {
    ...selectedUserPrefillAnswers,
    ...medicaidFormAnswers,
  };

  // Lock fields based on whether values are actually in Redux state (from user selection)
  const getTrimmedString = (value: unknown): string =>
    typeof value === "string" ? value.trim() : "";

  const hasFirstName = Boolean(
    getTrimmedString(effectiveMedicaidFormAnswers[clientFields.firstName]),
  );
  const hasLastName = Boolean(
    getTrimmedString(effectiveMedicaidFormAnswers[clientFields.lastName]),
  );
  const hasPhone = Boolean(
    getTrimmedString(
      effectiveMedicaidFormAnswers[clientFields.contact.cellPhone],
    ),
  );
  const hasState = Boolean(
    getTrimmedString(effectiveMedicaidFormAnswers.state),
  );

  // Lock all name/phone fields once they're populated, unlock when empty
  const shouldLockNamePhone = hasFirstName && hasLastName && hasPhone;
  // Lock state only when it has a value
  const shouldLockState = hasState;
  const prefillFormKey = [
    effectiveMedicaidFormAnswers[clientFields.firstName] || "",
    effectiveMedicaidFormAnswers[clientFields.middleName] || "",
    effectiveMedicaidFormAnswers[clientFields.lastName] || "",
    effectiveMedicaidFormAnswers[clientFields.contact.cellPhone] || "",
    effectiveMedicaidFormAnswers.state || "",
  ].join("|");

  const startMedicaidDraft = async () => {
    if (!selectedUser?.id) {
      logger.notify(TYPE.ERROR, "Please select an existing client email.");
      return;
    }

    controls?.busy(true);
    try {
      await post(
        API.PARTNER_MEDICAID_LEAD(),
        populateMedicaidAnswers(effectiveMedicaidFormAnswers, selectedUser.id),
      );
    } catch (error) {
      const defaultErrorMessage =
        "Adding Medicaid qualification failed. Please, try again.";
      if (axios.isAxiosError(error) && error.response?.status === 400) {
        logger.notify(
          TYPE.ERROR,
          error?.response
            ? new ApiError(defaultErrorMessage, error.response)
            : defaultErrorMessage,
          error,
        );
      } else {
        logger.notify(TYPE.ERROR, defaultErrorMessage, error);
      }
    } finally {
      controls?.busy(false);
      controls?.close();
    }
  };

  return (
    <>
      <Box mb={2}>
        <Autocomplete<PartnerUserEmailSuggestion>
          options={emailOptions}
          filterOptions={(options) => options}
          loading={isSearchingUsers}
          value={selectedUser}
          inputValue={emailInputValue}
          onInputChange={(_event, value) => {
            setEmailInputValue(value);
            if (selectedUser?.email !== value) {
              setSelectedUser(null);
            }
          }}
          onChange={(_event, value) => {
            setSelectedUser(value);
            setEmailInputValue(value?.email || "");
            const payload = {
              [clientFields.firstName]: value?.firstName || "",
              [clientFields.middleName]: value?.middleName || "",
              [clientFields.lastName]: value?.lastName || "",
              [clientFields.contact.cellPhone]: value?.phoneNumber || "",
              state: value?.state || "",
            };
            dispatch(medicaidDraftingAnswersChanged(payload));
          }}
          getOptionLabel={(option) => option.email}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          renderOption={(props, option) => (
            <li {...props}>
              <Box>
                <Typography variant="body2">{option.email}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {[option.firstName, option.lastName]
                    .filter(Boolean)
                    .join(" ")}
                </Typography>
              </Box>
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              required
              label="Client E-mail"
              placeholder="Start typing client email"
            />
          )}
        />
      </Box>
      <QuestionnaireForm
        customFormProps={{
          showSaveAndExitButton: false,
          formKey: `medicaid-prefill-${prefillFormKey}`,
        }}
        onSavePageAnswers={() => {}}
        onChange={handleChange}
        answers={{
          [QUESTIONNAIRE_ID]: effectiveMedicaidFormAnswers,
        }}
        questionsBySubquestionnaire={{
          [QUESTIONNAIRE_ID]: {
            state: {
              id: "state",
              data: {
                descriptions: [{ label: "Jurisdiction (State)" }],
                required: true,
                readOnly: shouldLockState,
                definition: {
                  type: QuestionType.STATE,
                  maxSuggestedItems: 51,
                },
              },
            },
            [clientFields.firstName]: {
              id: clientFields.firstName,
              data: {
                descriptions: [{ label: "Client First name" }],
                required: true,
                readOnly: shouldLockNamePhone,
                definition: { type: QuestionType.TEXT },
              },
            },
            [clientFields.middleName]: {
              id: clientFields.middleName,
              data: {
                descriptions: [{ label: "Client Middle name" }],
                required: false,
                readOnly: shouldLockNamePhone,
                definition: { type: QuestionType.TEXT },
              },
            },
            [clientFields.lastName]: {
              id: clientFields.lastName,
              data: {
                descriptions: [{ label: "Client Last name" }],
                required: true,
                readOnly: shouldLockNamePhone,
                definition: { type: QuestionType.TEXT },
              },
            },
            [clientFields.contact.cellPhone]: {
              id: clientFields.contact.cellPhone,
              data: {
                required: true,
                readOnly: shouldLockNamePhone,
                definition: {
                  type: QuestionType.TEXT,
                  subtype: TextFieldType.TEL,
                },
                descriptions: [{ label: "Client Phone number" }],
              },
            },
          },
        }}
        questionnairePages={[
          {
            id: "default_page",
            questionnaireId: QUESTIONNAIRE_ID,
            caption: "",
            sections: [
              {
                caption: "",
                questions: [
                  "state",
                  clientFields.firstName,
                  clientFields.middleName,
                  clientFields.lastName,
                  clientFields.contact.cellPhone,
                ],
              },
            ],
          },
        ]}
      />
      <TabbedButton
        onClick={startMedicaidDraft}
        label="Create Medicaid Draft"
        addClassName={cx("begin-draft-button")}
        disabled={!selectedUser?.id}
      />
    </>
  );
};

export default MedicaidDraftAdd;
